import i18next, { type i18n as I18n } from "i18next";
import ICU from "i18next-icu";
import { readCachedSet, writeCachedSet } from "./cache";
import { resolveLanguage } from "./language";
import { DEFAULT_BASE_PATH, fetchManifest, loadCatalogSet, PREVIEW_BASE_PATH, type LoaderOptions } from "./loader";
import {
  CatalogError,
  type CatalogErrorCode,
  type CatalogSet,
  type CatalogStorage,
  type ClientIdentity,
  type DigestProvider,
  type EmbeddedCatalog,
  type FetchLike,
  type Manifest,
} from "./types";

export type CatalogSource = "embedded" | "cache" | "remote";
/** product: the published release, its cached set or the embedded catalog; preview: the private tester release, held in memory only. */
export type CatalogChannel = "product" | "preview";
export type FailureReason = CatalogErrorCode | "unexpected";

/** Diagnostics for observability; carries keys, codes and revisions, never message parameters or personal values. */
export type LocalizationEvent =
  | { readonly type: "activated"; readonly language: string; readonly revision: string; readonly source: CatalogSource }
  | { readonly type: "load_failed"; readonly reason: FailureReason; readonly language: string | null }
  /** A delivery failure left the client on its cached set or embedded catalog (not for an unpublished choice). */
  | { readonly type: "catalog_fallback"; readonly source: "cache" | "embedded" }
  /**
   * The verified manifest of this request does not publish the explicit
   * (profile) language, so the browser rule or the base language decided; at
   * most once per request. Cached sets and offline paths never report it.
   */
  | { readonly type: "preference_unavailable" }
  | { readonly type: "cache_rejected"; readonly reason: "storage" | "corrupt" | "foreign" }
  | { readonly type: "cache_write_failed" }
  | { readonly type: "missing_key"; readonly key: string }
  | { readonly type: "format_error"; readonly key: string }
  /** The text came from a later language of the active language's fallback chain (untranslated in the active one). */
  | { readonly type: "message_fallback"; readonly key: string };

export interface LocalizationSnapshot {
  readonly language: string;
  readonly revision: string;
  readonly source: CatalogSource;
  /** loading while the latest request runs; error when it failed (the previous catalog stays active). */
  readonly status: "idle" | "loading" | "ready" | "error";
  readonly error: FailureReason | null;
  /** Published languages the client may offer (from the latest product manifest, else the active product set); a preview never changes them. */
  readonly languages: readonly string[];
  readonly channel: CatalogChannel;
  /** Languages of the active preview release, drafts included; empty outside preview. Never a profile choice. */
  readonly previewLanguages: readonly string[];
}

export type ChangeResult =
  | { readonly ok: true; readonly language: string; readonly revision: string; readonly source: CatalogSource }
  | { readonly ok: false; readonly reason: FailureReason | "superseded"; readonly status?: number };

/** Message parameters are untrusted plain values; they are never interpreted as markup. */
export type MessageParams = Readonly<Record<string, string | number>>;

export interface LanguageIntent {
  /** Explicit profile language; null or absent means automatic. */
  readonly explicit?: string | null;
  /** navigator.languages or equivalent, most preferred first. */
  readonly browser?: readonly string[];
  /**
   * The server's resolution (bootstrap `ui_language`) for this client
   * contract. It decides only when this runtime's own verified manifest
   * publishes it; otherwise, and offline, the client rule above applies.
   */
  readonly projected?: string | null;
}

export interface LocalizationRuntime {
  readonly i18n: I18n;
  readonly identity: ClientIdentity;
  getSnapshot(): LocalizationSnapshot;
  subscribe(listener: () => void): () => void;
  /** Idempotent: the first call restores the cache and loads the release; later calls return the same promise. */
  start(intent?: LanguageIntent): Promise<ChangeResult>;
  setLanguage(language: string): Promise<ChangeResult>;
  /**
   * Replaces the given parts of the language intent (`explicit`, null meaning
   * automatic, and/or `browser`) and activates the language it resolves to.
   * The last request wins over start(), setLanguage() and earlier intents;
   * nothing is recreated or remounted. Before start() it only records the
   * intent, which start() then uses, and settles with start()'s result.
   */
  setIntent(intent: LanguageIntent): Promise<ChangeResult>;
  /** Revalidates the pointer and activates a new compatible revision of the current language. */
  refresh(): Promise<ChangeResult>;
  /**
   * Switches the catalog source, like a language change: the whole verified
   * set is activated at once and the last request of any kind wins.
   *
   * A request shows `language` from the private preview release, loaded with
   * `fetch` and never written to the cache. Entry failure keeps the active
   * product set; a failed switch inside preview keeps the active preview set.
   * While preview is requested, setIntent(), setLanguage() and refresh() only
   * record the product intent. null returns to the product resolution of
   * that intent, revalidating the pointer; failing that, the product set
   * stays, or its last-good cached set or the embedded catalog replaces the
   * preview, which never stays live after a product request. Requires start().
   */
  setPreview(request: PreviewRequest | null): Promise<ChangeResult>;
  t(key: string, params?: MessageParams): string;
}

export interface PreviewRequest {
  /** A language the preview release publishes for this client. */
  readonly language: string;
  /** Account-bound fetch for the private preview paths of this one request (preview.ts). */
  readonly fetch: FetchLike;
}

export interface RuntimeOptions {
  /** Build-time catalog of this client (`@voltie/localization-web/embedded`). */
  readonly embedded: EmbeddedCatalog;
  readonly basePath?: string;
  readonly fetch?: FetchLike;
  /** null disables the cache; default: localStorage when usable. */
  readonly storage?: CatalogStorage | null;
  readonly crypto?: DigestProvider;
  readonly origin?: string;
  readonly timeoutMs?: number;
  readonly onEvent?: (event: LocalizationEvent) => void;
}

const SEMANTIC_KEY = /^[a-z]+(\.[a-z0-9_]+){1,4}$/;

/** Thrown inside a request that a later request replaced; run() reports it as superseded. */
class Superseded extends Error {}

function defaultStorage(): CatalogStorage | null {
  try {
    const storage = globalThis.localStorage;
    return typeof storage?.getItem === "function" ? storage : null;
  } catch {
    // Access itself throws when storage is blocked (SecurityError).
    return null;
  }
}

const missingCrypto: DigestProvider = {
  subtle: {
    digest: () => Promise.reject(new CatalogError("integrity", "Web Crypto is unavailable; catalogs cannot be verified")),
  },
};

function identityOf(embedded: EmbeddedCatalog): ClientIdentity {
  const namespaces = Object.keys(embedded?.namespaces ?? {}).sort();
  if (embedded?.schema_version !== 1 || !embedded.client_id || !embedded.language || namespaces.length === 0) {
    throw new TypeError("invalid embedded catalog: build it with the voltieLocalization() Vite plugin");
  }
  return {
    clientId: embedded.client_id,
    contractVersion: embedded.contract_version,
    contractSha256: embedded.contract_sha256,
    baseLanguage: embedded.language,
    namespaces,
    embeddedRevision: embedded.catalog_revision,
  };
}

/**
 * Creates the one localization runtime of a client. Create it once at module
 * scope, outside React, and pass it to LocalizationProvider: the i18next
 * instance, its subscriptions and the active catalog then survive every
 * render, route change and language switch. The embedded English catalog is
 * active synchronously, so the first render never waits for the network.
 */
export function createLocalizationRuntime(options: RuntimeOptions): LocalizationRuntime {
  const { embedded } = options;
  const identity = identityOf(embedded);
  const base = identity.baseLanguage;
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const loader: LoaderOptions = {
    fetch: options.fetch ?? ((input, init) => globalThis.fetch(input, init)),
    crypto: options.crypto ?? (globalThis.crypto?.subtle ? globalThis.crypto : missingCrypto),
    origin: options.origin ?? globalThis.location?.origin ?? "null",
    basePath: options.basePath ?? DEFAULT_BASE_PATH,
    timeoutMs: options.timeoutMs ?? 10_000,
    maxManifestBytes: 256 * 1024,
    maxArtifactBytes: 2 * 1024 * 1024,
  };

  const emit = (event: LocalizationEvent) => {
    try {
      options.onEvent?.(event);
    } catch {
      // A failing observer must not break rendering.
    }
  };
  // Counts missing keys and format errors, so t() can tell a message fallback from a failed lookup of the same call.
  let lookupProblems = 0;
  const reportLookup = (event: Extract<LocalizationEvent, { type: "missing_key" | "format_error" }>) => {
    lookupProblems += 1;
    emit(event);
  };

  let fallbackChains: Readonly<Record<string, readonly string[]>> = { [base]: [] };
  const i18n = i18next.createInstance();
  void i18n.use(ICU).init({
    lng: base,
    fallbackLng: (code: string) => [...(fallbackChains[code] ?? [base])],
    load: "currentOnly",
    ns: [...identity.namespaces],
    defaultNS: identity.namespaces[0],
    resources: { [base]: Object.fromEntries(identity.namespaces.map((ns) => [ns, { ...embedded.namespaces[ns] }])) },
    initAsync: false,
    keySeparator: false,
    nsSeparator: false,
    returnNull: false,
    // Output is plain text rendered by React, which escapes it; catalogs carry no markup (validator rule).
    interpolation: { escapeValue: false },
    react: { useSuspense: false, bindI18n: "languageChanged", bindI18nStore: "added removed" },
    parseMissingKeyHandler: (key: string) => {
      reportLookup({ type: "missing_key", key });
      return key;
    },
    i18nFormat: {
      // A message that cannot be formatted with the given parameters shows its
      // key instead of throwing or rendering partial text.
      parseErrorHandler: (_err: unknown, key: string) => {
        reportLookup({ type: "format_error", key });
        return key;
      },
    },
  });

  let snapshot: LocalizationSnapshot = {
    language: base,
    revision: embedded.catalog_revision,
    source: "embedded",
    status: "idle",
    error: null,
    languages: [base],
    channel: "product",
    previewLanguages: [],
  };
  const listeners = new Set<() => void>();
  const publish = (next: Partial<LocalizationSnapshot>) => {
    snapshot = { ...snapshot, ...next };
    for (const listener of [...listeners]) listener();
  };

  /** Swaps the whole catalog of a language chain in one synchronous step, then notifies once. */
  function activate(set: CatalogSet | null, source: CatalogSource, channel: CatalogChannel = "product"): void {
    const chain = set?.chain ?? [base];
    fallbackChains = set ? set.fallback : { [base]: [] };
    for (const lng of Object.keys(i18n.store.data)) {
      if (!chain.includes(lng)) for (const ns of identity.namespaces) i18n.removeResourceBundle(lng, ns);
    }
    for (const lng of chain) {
      for (const ns of identity.namespaces) {
        const remote = set?.bundles[lng]?.[ns];
        // Base language: the embedded catalog completes anything the release lacks.
        const data = lng === base ? { ...embedded.namespaces[ns], ...remote } : remote;
        i18n.removeResourceBundle(lng, ns);
        if (data) i18n.addResourceBundle(lng, ns, data, false, true);
      }
    }
    const language = set?.language ?? base;
    if (i18n.language !== language) void i18n.changeLanguage(language);
    const revision = set?.revision ?? embedded.catalog_revision;
    emit({ type: "activated", language, revision, source });
    publish({
      language,
      revision,
      source,
      channel,
      languages: channel === "product" ? (set?.languages ?? snapshot.languages) : snapshot.languages,
      previewLanguages: channel === "preview" && set ? set.languages : [],
    });
  }

  let sequence = 0;
  let inflight: AbortController | null = null;
  let manifest: Manifest | null = null;
  let intent: LanguageIntent = {};
  let started: Promise<ChangeResult> | null = null;
  let startWaiters: ((started: Promise<ChangeResult>) => void)[] = [];
  // Tab memory only. While previewing, the product intent is recorded but not applied.
  let previewing = false;
  // The channel of the latest request while it runs, and whether a product
  // request was superseded or a product intent recorded while previewing.
  let pending: CatalogChannel | null = null;
  let productStale = false;

  /** Fields present in `next` replace those of `base`; a present `explicit` or `projected` of undefined means none. */
  const mergeIntent = (base: LanguageIntent, next: LanguageIntent): LanguageIntent => ({
    ...base,
    ...("explicit" in next ? { explicit: next.explicit ?? null } : {}),
    ...("projected" in next ? { projected: next.projected ?? null } : {}),
    ...(next.browser !== undefined ? { browser: next.browser } : {}),
  });

  /**
   * The server projection when this release publishes it (it can never add a
   * language), else the client rule. `verifiedBy` is the request whose
   * verified manifest supplied `published`: only that manifest can confirm
   * that the explicit choice is not published, never a cached set.
   */
  function choose(published: readonly string[], baseLanguage: string, verifiedBy?: number): string {
    const { projected, ...rule } = intent;
    const resolution = resolveLanguage({ ...rule, published, base: baseLanguage });
    if (resolution.source === "fallback_unavailable" && verifiedBy !== undefined && current(verifiedBy)) emit({ type: "preference_unavailable" });
    if (projected && published.includes(projected)) return projected;
    return resolution.language;
  }

  function begin(channel: CatalogChannel = "product"): { id: number; signal: AbortSignal } {
    if (channel === "preview" && pending === "product") productStale = true;
    pending = channel;
    sequence += 1;
    inflight?.abort();
    inflight = new AbortController();
    publish({ status: "loading", error: null });
    return { id: sequence, signal: inflight.signal };
  }

  const current = (id: number) => id === sequence;
  const okResult = (): ChangeResult => ({ ok: true, language: snapshot.language, revision: snapshot.revision, source: snapshot.source });
  const isActive = (revision: string, language: string, channel: CatalogChannel = "product") =>
    snapshot.channel === channel && snapshot.source !== "embedded" && snapshot.revision === revision && snapshot.language === language;
  /** After a failed delivery: whether the client now serves its cached set or embedded catalog instead of a release. */
  const noteFallback = (reason: FailureReason) => {
    if (reason !== "language_unavailable" && snapshot.source !== "remote") emit({ type: "catalog_fallback", source: snapshot.source });
  };

  // The verified set this runtime last read from or wrote to the cache record,
  // and the one of another language it replaced there. The record holds one
  // set per client contract, so a start that resolves before the profile
  // language is known stores the browser language's set over the profile's;
  // the replaced set stays here in memory as that language's last-good set.
  let recorded: CatalogSet | null = null;
  let displaced: CatalogSet | null = null;

  function persist(set: CatalogSet): void {
    if (recorded && recorded.language !== set.language) displaced = recorded;
    recorded = set;
    if (!writeCachedSet(storage, identity, set)) emit({ type: "cache_write_failed" });
  }

  /** The last-good verified set of `language`: the cache record, else the one it replaced in this session (stored again). */
  async function cachedFor(id: number, language: string): Promise<CatalogSet | null> {
    const cached = await readCachedSet(storage, identity, loader.crypto);
    if (!current(id)) return null;
    if (cached.set) {
      if (cached.set.language === language) return cached.set;
    } else if (cached.rejected !== "absent") {
      emit({ type: "cache_rejected", reason: cached.rejected });
    }
    if (displaced?.language !== language) return null;
    const restored = displaced;
    persist(restored);
    return restored;
  }

  /**
   * Runs one request. Only the latest request may activate anything; an
   * older one that finishes later is reported as superseded and ignored.
   * On failure the active catalog stays, or `offline` picks the last valid
   * cached set or the embedded base for the requested language.
   */
  async function run(id: number, language: string | null, load: () => Promise<CatalogSet | null>, offline?: () => Promise<boolean>): Promise<ChangeResult> {
    try {
      const set = await load();
      if (!current(id)) return { ok: false, reason: "superseded" };
      if (set) {
        activate(set, "remote");
        persist(set);
      }
      settle();
      publish({ status: "ready" });
      return okResult();
    } catch (e) {
      if (!current(id)) return { ok: false, reason: "superseded" };
      const reason: FailureReason = e instanceof CatalogError ? e.code : "unexpected";
      emit({ type: "load_failed", reason, language });
      if (offline && reason !== "language_unavailable" && reason !== "contract_incompatible") {
        try {
          if ((await offline()) && current(id)) {
            settle();
            noteFallback(reason);
            publish({ status: "error", error: reason });
            return okResult();
          }
        } catch {
          // The offline path is best effort; the active catalog stays.
        }
      }
      if (current(id)) {
        settle();
        noteFallback(reason);
        publish({ status: "error", error: reason });
      }
      return { ok: false, reason };
    }
  }

  /** A settled product request: the private preview set never outlives it. */
  function settle(): void {
    pending = null;
    productStale = false;
    if (snapshot.channel === "preview") activate(null, "embedded");
  }

  /**
   * The manifest request `id` works with. A fetched manifest becomes shared
   * state only while `id` is still the latest request: a stale response (a
   * fetch that ignored its abort signal) must not change the published
   * languages or the manifest later requests reuse.
   */
  async function latestManifest(id: number, signal: AbortSignal, revalidate: boolean): Promise<Manifest> {
    if (manifest && !revalidate) return manifest;
    const fetched = await fetchManifest(loader, identity, signal);
    if (!current(id)) throw new Superseded();
    manifest = fetched;
    if (fetched.languages.join() !== snapshot.languages.join()) publish({ languages: fetched.languages });
    return fetched;
  }

  function start(startIntent: LanguageIntent = {}): Promise<ChangeResult> {
    if (started) return started;
    // An intent recorded by setIntent() before the start is newer than the start's own.
    intent = mergeIntent(startIntent, intent);
    started = (async () => {
      const { id, signal } = begin();
      // Offline start: the last valid cached set of the language the intent
      // resolves to among its own published languages.
      const cached = await readCachedSet(storage, identity, loader.crypto);
      // A setLanguage() or refresh() during the cache read now owns the state.
      if (!current(id)) return { ok: false, reason: "superseded" };
      if (cached.set) {
        recorded = cached.set;
        const wanted = choose(cached.set.languages, base);
        if (wanted === cached.set.language) activate(cached.set, "cache");
      } else if (cached.rejected !== "absent") {
        emit({ type: "cache_rejected", reason: cached.rejected });
      }
      return run(id, null, async () => {
        const m = await latestManifest(id, signal, true);
        const language = choose(m.languages, m.base_language, id);
        if (isActive(m.catalog_revision, language)) return null;
        return loadCatalogSet(loader, m, identity, language, signal);
      });
    })();
    for (const waiter of startWaiters) waiter(started);
    startWaiters = [];
    return started;
  }

  /** While previewing, a product change is only recorded; the exit applies it. */
  function deferred(): Promise<ChangeResult> | null {
    if (!previewing) return null;
    productStale = true;
    return Promise.resolve(okResult());
  }

  function setLanguage(language: string): Promise<ChangeResult> {
    intent = { ...intent, explicit: language, projected: null };
    const recorded = deferred();
    if (recorded) return recorded;
    const { id, signal } = begin();
    return run(
      id,
      language,
      async () => {
        const m = await latestManifest(id, signal, false);
        if (!m.languages.includes(language)) throw new CatalogError("language_unavailable", `${language} is not published`);
        if (isActive(m.catalog_revision, language)) return null;
        return loadCatalogSet(loader, m, identity, language, signal);
      },
      async () => {
        const cached = await cachedFor(id, language);
        if (!current(id)) return false;
        if (cached) activate(cached, "cache");
        else if (language === base) activate(null, "embedded");
        else return false;
        return true;
      },
    );
  }

  function setIntent(next: LanguageIntent): Promise<ChangeResult> {
    intent = mergeIntent(intent, next);
    if (!started) return new Promise((resolve) => startWaiters.push(resolve));
    return deferred() ?? product(false);
  }

  /** Activates what the product intent resolves to in the product release. */
  function product(revalidate: boolean): Promise<ChangeResult> {
    const { id, signal } = begin();
    let wanted: string | null = null;
    return run(
      id,
      null,
      async () => {
        const m = await latestManifest(id, signal, revalidate);
        wanted = choose(m.languages, m.base_language, id);
        if (isActive(m.catalog_revision, wanted)) return null;
        return loadCatalogSet(loader, m, identity, wanted, signal);
      },
      async () => {
        // Leaving preview offline: a live product set stays as it is.
        if (revalidate && snapshot.channel === "product") return true;
        const language = wanted ?? choose(snapshot.languages, base);
        const cached = await cachedFor(id, language);
        if (!current(id)) return false;
        if (cached) activate(cached, "cache");
        else if (language === base) activate(null, "embedded");
        else return false;
        return true;
      },
    );
  }

  function refresh(): Promise<ChangeResult> {
    // The product pointer is revalidated when the preview ends.
    if (previewing) return Promise.resolve(okResult());
    const { id, signal } = begin();
    return run(id, snapshot.language, async () => {
      const m = await latestManifest(id, signal, true);
      const language = m.languages.includes(snapshot.language) ? snapshot.language : choose(m.languages, m.base_language, id);
      if (isActive(m.catalog_revision, language)) return null;
      return loadCatalogSet(loader, m, identity, language, signal);
    });
  }

  function setPreview(request: PreviewRequest | null): Promise<ChangeResult> {
    if (!started) return Promise.resolve({ ok: false, reason: "unexpected" });
    previewing = request !== null;
    return request ? preview(request) : product(true);
  }

  async function preview(request: PreviewRequest): Promise<ChangeResult> {
    const { id, signal } = begin("preview");
    // The private source: its own base path and account-bound fetch, a fresh
    // manifest for every request, and no cache in either direction.
    const source: LoaderOptions = { ...loader, basePath: PREVIEW_BASE_PATH, fetch: request.fetch };
    try {
      const m = await fetchManifest(source, identity, signal);
      if (!current(id)) return { ok: false, reason: "superseded" };
      if (!m.languages.includes(request.language)) throw new CatalogError("language_unavailable", `${request.language} is not in the preview`);
      if (!isActive(m.catalog_revision, request.language, "preview")) {
        const set = await loadCatalogSet(source, m, identity, request.language, signal);
        if (!current(id)) return { ok: false, reason: "superseded" };
        activate(set, "remote", "preview");
      }
      pending = null;
      publish({ status: "ready" });
      return okResult();
    } catch (e) {
      if (!current(id)) return { ok: false, reason: "superseded" };
      const reason: FailureReason = e instanceof CatalogError ? e.code : "unexpected";
      const status = e instanceof CatalogError ? e.status : null;
      emit({ type: "load_failed", reason, language: request.language });
      pending = null;
      publish({ status: "error", error: reason });
      if (snapshot.channel === "product") {
        // Entry failed: nothing private is live and the product intent decides again.
        previewing = false;
        if (productStale) void product(false);
      }
      return status === null ? { ok: false, reason } : { ok: false, reason, status };
    }
  }

  function t(key: string, params?: MessageParams): string {
    const ns = key.slice(0, key.indexOf("."));
    if (!SEMANTIC_KEY.test(key) || !identity.namespaces.includes(ns)) {
      reportLookup({ type: "missing_key", key });
      return key;
    }
    try {
      const problems = lookupProblems;
      const { res, usedLng } = i18n.t(key, { ns, replace: { ...params }, returnDetails: true });
      if (typeof res !== "string") return key;
      if (lookupProblems === problems && usedLng && usedLng !== i18n.language) emit({ type: "message_fallback", key });
      return res;
    } catch {
      reportLookup({ type: "format_error", key });
      return key;
    }
  }

  return {
    i18n,
    identity,
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start,
    setLanguage,
    setIntent,
    refresh,
    setPreview,
    t,
  };
}
